/**
 * What the editor's chips and blocks do when clicked: look up their plain labels, remove
 * themselves, turn a "write this" chip into editable words, and change a condition.
 */
import { useCallback } from 'react';
import { useEditorState, type NodeViewProps } from '@tiptap/react';
import type { CatalogField, ReplyCatalog } from '../api/api';

const catalogOf = (props: NodeViewProps) =>
  (props.extension.options as { catalog: ReplyCatalog }).catalog;

export function useFieldChip(props: NodeViewProps) {
  const name = String(props.node.attrs.name ?? '');
  const field: CatalogField | undefined = catalogOf(props).fields.find(
    candidate => candidate.name === name,
  );
  const label = field?.label ?? name;

  /** Swaps the chip for its label as plain words, selected, so typing replaces it. */
  const replaceWithWords = useCallback(() => {
    const pos = props.getPos();
    if (typeof pos !== 'number') return;
    props.editor
      .chain()
      .focus()
      .insertContentAt({ from: pos, to: pos + 1 }, label)
      .setTextSelection({ from: pos, to: pos + label.length })
      .run();
  }, [label, props]);

  /** Removes the chip, and one of the two spaces it sat between, so no double space is left. */
  const remove = useCallback(() => {
    const pos = props.getPos();
    if (typeof pos !== 'number') return;
    const { doc } = props.editor.state;
    const before = pos > 0 ? doc.textBetween(pos - 1, pos) : '';
    const after = pos + 2 <= doc.content.size ? doc.textBetween(pos + 1, pos + 2) : '';
    const to = before === ' ' && after === ' ' ? pos + 2 : pos + 1;
    props.editor.chain().focus().deleteRange({ from: pos, to }).run();
  }, [props]);

  return {
    label,
    kind: field?.kind ?? 'none',
    meaning: field?.meaning ?? null,
    remove,
    replaceWithWords,
  };
}

export function useConditionBlock(props: NodeViewProps) {
  const catalog = catalogOf(props);
  const flag = String(props.node.attrs.flag ?? '');
  const setFlag = useCallback((next: string) => props.updateAttributes({ flag: next }), [props]);
  return { flag, setFlag, conditions: catalog.conditions, remove: props.deleteNode };
}

/** The "If …" label of a condition side, following its block's condition as it changes. */
export function useConditionSide(props: NodeViewProps) {
  const side = props.node.attrs.side === 'otherwise' ? 'otherwise' : 'then';
  const flag = useEditorState({
    editor: props.editor,
    selector: ({ editor }) => {
      const pos = props.getPos();
      if (typeof pos !== 'number' || !editor) return null;
      return String(editor.state.doc.resolve(pos).parent.attrs.flag ?? '');
    },
  });
  const conditions = (
    props.editor.extensionManager.extensions.find(extension => extension.name === 'condition')
      ?.options as { catalog: ReplyCatalog } | undefined
  )?.catalog.conditions;
  const label = conditions?.find(condition => condition.name === flag)?.label ?? flag ?? '';
  return { side, title: side === 'then' ? `If ${label}` : 'Otherwise' } as const;
}
